import * as React from 'react'

import {
  BucketType,
  ResponseAvgDaysPerFileTrend,
  ResponseResolutionRateTrend,
  ResponseReviewTimeTrend,
  TimeRange,
} from '@/schema'

import { PeriodMetricKey } from '../constants'
import { useDashboardStore } from '../store'

import {
  useGetAvgDaysPerFileTrend,
  useGetResolutionRateTrend,
  useGetReviewTimeTrend,
} from './index'

type TPeriodMetricTrendData =
  | ResponseReviewTimeTrend
  | ResponseResolutionRateTrend
  | ResponseAvgDaysPerFileTrend
  | null

type TUsePeriodMetricProps = {
  key: PeriodMetricKey
}

type TUsePeriodMetricResult = {
  aggregationPeriod: BucketType
  timeRange: TimeRange
  trendData: TPeriodMetricTrendData
  isLoading: boolean
}

export const usePeriodMetric = (
  props: TUsePeriodMetricProps
): TUsePeriodMetricResult => {
  const { key } = props
  const {
    filters: { companyId },
    aggregationPeriod,
    timeRange,
  } = useDashboardStore()

  const {
    data: { data: [reviewTimeTrendData] = [] } = {},
    isLoading: isLoadingReviewTimeTrend,
  } = useGetReviewTimeTrend(
    {
      bucket_type: aggregationPeriod,
      time_range: timeRange,
      company_id: companyId ?? undefined,
    },
    { query: { enabled: key === PeriodMetricKey.REVIEW_TIME_TREND } }
  )

  const {
    data: { data: [resolutionRateTrendData] = [] } = {},
    isLoading: isLoadingResolutionRateTrend,
  } = useGetResolutionRateTrend(
    {
      bucket_type: aggregationPeriod,
      time_range: timeRange,
      company_id: companyId ?? undefined,
    },
    { query: { enabled: key === PeriodMetricKey.RESOLUTION_RATE_TREND } }
  )

  const {
    data: { data: [avgDaysPerFileTrendData] = [] } = {},
    isLoading: isLoadingAvgDaysPerFileTrend,
  } = useGetAvgDaysPerFileTrend(
    {
      bucket_type: aggregationPeriod,
      time_range: timeRange,
      company_id: companyId ?? undefined,
    },
    { query: { enabled: key === PeriodMetricKey.AVG_DAYS_PER_FILE_TREND } }
  )

  const isLoading = React.useMemo(() => {
    switch (key) {
      case PeriodMetricKey.REVIEW_TIME_TREND:
        return isLoadingReviewTimeTrend
      case PeriodMetricKey.RESOLUTION_RATE_TREND:
        return isLoadingResolutionRateTrend
      case PeriodMetricKey.AVG_DAYS_PER_FILE_TREND:
        return isLoadingAvgDaysPerFileTrend
      default:
        return false
    }
  }, [
    key,
    isLoadingReviewTimeTrend,
    isLoadingResolutionRateTrend,
    isLoadingAvgDaysPerFileTrend,
  ])

  const trendData: TPeriodMetricTrendData = React.useMemo(() => {
    switch (key) {
      case PeriodMetricKey.REVIEW_TIME_TREND:
        return reviewTimeTrendData
      case PeriodMetricKey.RESOLUTION_RATE_TREND:
        return resolutionRateTrendData
      case PeriodMetricKey.AVG_DAYS_PER_FILE_TREND:
        return avgDaysPerFileTrendData
      default:
        return null
    }
  }, [
    key,
    reviewTimeTrendData,
    resolutionRateTrendData,
    avgDaysPerFileTrendData,
  ])

  return React.useMemo(
    () => ({ aggregationPeriod, timeRange, trendData, isLoading }),
    [aggregationPeriod, timeRange, trendData, isLoading]
  )
}
